import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { InfoCircleFill } from 'react-bootstrap-icons';
import { useAppDispatch, useAppSelector } from '../../../../hooks/redux';
import { selectSchedule } from '../../../../features/schedule/scheduleSelectors';
import { updateAllScheduleConstraints } from '../../../../features/schedule/scheduleSlice';
import { TagIdsEnum } from '../../../../constant/navigation';
import {
  generateInitialScheduleValues,
  generateScheduleSchema,
  getTimeDateFromFormikInput,
  getTournamentTimezone,
  ROUND_KEYS,
  ScheduleFormikValues,
} from './schedule.helpers';
import { CustomButton } from '../../../../components';
import { OptInError } from './FormComponents/OptInCheckBoxArray';
import { ScheduleLayout } from './NewScheduleLayout/ScheduleLayout';
import {
  createScheduleAction,
  skipToManualScheduleAction,
} from '../../../../features/schedule/scheduleActions';
import { ClassFormatsSetup } from './FormComponents/ClassFormatsSetup';
import { DayVenuesSetup } from './FormComponents/DayVenuesSetup';
import { GlobalRulesSetup } from './FormComponents/GlobalRulesSetup';
import { selectIsRunner, selectIsSuperAdmin} from '../../../../features/currentUser/currentUserSelectors';

export const ScheduleTag = () => {
  const { t } = useTranslation();
  const prefix = 'creativeDetail-';
  const dispatch = useAppDispatch();
  const { scheduleConstraints, scheduleConstraintsGiven, tournamentInfo } =
    useAppSelector(selectSchedule);

  const timeZone = getTournamentTimezone(tournamentInfo?.country, tournamentInfo?.timeZone);

  const isRunner = useAppSelector(selectIsRunner);
  const isSuperAdmin = useAppSelector(selectIsSuperAdmin);
  const canUseScheduler = isRunner || isSuperAdmin;

  const [activeTab, setActiveTab] = useState<'global' | 'venues' | 'classes'>('venues');

  const initialFormikValues = scheduleConstraints
    ? generateInitialScheduleValues(scheduleConstraints, timeZone)
    : null;

  const scheduleSchema =
    scheduleConstraints?.days &&
    Yup.object().shape(generateScheduleSchema(scheduleConstraints?.days!, t));

  const buildConstraints = (values: any) => {
    if (!scheduleConstraints || !tournamentInfo) return null;

    const updatedDays = scheduleConstraints.days.map((day) => {
      return {
        dayKey: day.dayKey,
        startTime: getTimeDateFromFormikInput(
          values['startOfDay-' + day.dayKey],
          day.startTime,
          timeZone
        ),
        endTime: getTimeDateFromFormikInput(
          values['endOfDay-' + day.dayKey],
          day.startTime,
          timeZone,
          { referenceStartIso: day.startTime }
        ),
        defaultMatchLength: 25,
        defaultPauseLength: 5,
        classes: (tournamentInfo.classes ?? []).filter((clazz) => {
          if (values['all-class-' + day.dayKey]) return true;
          return (
            (values['class-day-' + day.dayKey] as string[]).includes(clazz.id.toString()) ||
            (clazz.parentClassId &&
              (values['class-day-' + day.dayKey] as string[]).includes(
                clazz.parentClassId.toString()
              ))
          );
        }),
        classOptions: [],
        courts: (tournamentInfo.courts ?? []).filter((court) => {
          if (values['all-court-' + day.dayKey]) return true;
          return (values['court-day-' + day.dayKey] as string[]).includes(court.id.toString());
        }),
      };
    });

    const cleanedOverrides: Record<number, any> = {};

    if (values.classOverrides) {
      Object.keys(values.classOverrides).forEach((classIdStr) => {
        const classId = Number(classIdStr);
        const raw = values.classOverrides[classIdStr];
        const parseNum = (val: any) =>
          val !== '' && val !== null && val !== undefined ? Number(val) : undefined;

        const matchLengths: Record<string, number> = {};
        if (raw.customDurations && Array.isArray(raw.customDurations)) {
          raw.customDurations.forEach((item: any) => {
            if (item.roundKey && item.duration) {
              matchLengths[item.roundKey] = Number(item.duration);
            }
          });
        }

        cleanedOverrides[classId] = {
          event_priority: parseNum(raw.event_priority),
          span_limit: parseNum(raw.span_limit),
          gap_group_group: parseNum(raw.gap_group_group),
          gap_group_ko: parseNum(raw.gap_group_ko),
          gap_ko_ko: parseNum(raw.gap_ko_ko),
          matchLengths: matchLengths,
        };

        Object.keys(cleanedOverrides[classId]).forEach((key) => {
          if (cleanedOverrides[classId][key] === undefined) delete cleanedOverrides[classId][key];
        });
        if (Object.keys(cleanedOverrides[classId].matchLengths).length === 0)
          delete cleanedOverrides[classId].matchLengths;
        if (Object.keys(cleanedOverrides[classId]).length === 0) delete cleanedOverrides[classId];
      });
    }

    const globalMatchLengths: Record<string, number> = {};
    ROUND_KEYS.forEach(({ key }) => {
      if (key !== 'knockout_default') {
        globalMatchLengths[key] = values[`globalMatchLength_${key}`];
      }
    });

    return {
      ...scheduleConstraints,
      globalMatchLengths,
      allow_split: values.allow_split,
      allow_alignment: values.allow_alignment,
      pull_to_lower_court: values.pull_to_lower_court,
      gap_group_group: values.gap_group_group,
      max_gap_group_group: values.max_gap_group_group,
      gap_group_ko: values.gap_group_ko,
      max_gap_group_ko: values.max_gap_group_ko,
      gap_ko_ko: values.gap_ko_ko,
      max_gap_ko_ko: values.max_gap_ko_ko,
      max_span_limit: values.max_span_limit,
      s1_time_limit: values.s1_time_limit,
      s2_time_limit: values.s2_time_limit,
      stream_courts: values.stream_courts ? values.stream_courts.map(Number) : [],
      max_chunk_size: values.max_chunk_size,
      number_of_chunk_splits: values.number_of_chunk_splits,
      classOverrides: cleanedOverrides,
      days: updatedDays,
      scheduleTimezone: timeZone,
    };
  };

  const handleSubmit = (values: any) => {
    const updatedConstraints = buildConstraints(values);
    if (updatedConstraints) {
      dispatch(updateAllScheduleConstraints(updatedConstraints));
      dispatch(createScheduleAction(t));
    }
  };

  const handleSkipToManual = (values: any) => {
    const updatedConstraints = buildConstraints(values);
    if (updatedConstraints) {
      dispatch(updateAllScheduleConstraints(updatedConstraints));
      dispatch(skipToManualScheduleAction());
    }
  };

  return (
    <div className="container-auto">
      {scheduleConstraintsGiven ? (
        <ScheduleLayout />
      ) : !initialFormikValues && !initialFormikValues ? (
        <div>Loading constraints...</div>
      ) : (
        !scheduleConstraintsGiven &&
        initialFormikValues && (
          <Formik
            initialValues={initialFormikValues}
            validationSchema={scheduleSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, values }) => {
              const skipToManualHelperText = t(
                prefix + 'skipToManualHelper',
                'Saves your constraint settings and any match placements to the server, skips automatic generation, and opens the manual schedule editor. Matches without a court and time stay in the unscheduled list.'
              );
              const scheduleTournamentHelperText = t(
                prefix + 'scheduleTournamentHelper',
                'Saves your constraint settings and automatically builds the tournament schedule. When it is ready, the schedule opens in the editor; you may briefly see progress while it runs.'
              );
              return (
              <Form autoComplete="off" id={TagIdsEnum.SCHEDULE}>
                <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                  {canUseScheduler && (
                    <div>
                      <CustomButton
                        btnText={t(prefix + 'scheduleBtn', 'Schedule Tournament')}
                        type="submit"
                        variant="outline-dark"
                        className="d-flex align-items-center gap-2 m-0"
                        title={scheduleTournamentHelperText}
                      >
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>{scheduleTournamentHelperText}</Tooltip>}
                        >
                          <InfoCircleFill />
                        </OverlayTrigger>
                      </CustomButton>
                    </div>
                  )}
                  
                  <div className="border py-1 d-flex flex-row tagContainer flex-grow-1">
                    <div
                      className={`d-flex flex-grow-1 justify-content-center align-items-center py-1 mx-1 btnTabItem ${
                        activeTab === 'venues' ? 'activeBtn' : ''
                      }`}
                      onClick={() => setActiveTab('venues')}
                    >
                      <label className="mb-0 w-100 text-center" role="button">
                        Days & Venues
                      </label>
                    </div>

                    <div
                      className={`d-flex flex-grow-1 justify-content-center align-items-center py-1 mx-1 btnTabItem ${
                        activeTab === 'classes' ? 'activeBtn' : ''
                      }`}
                      onClick={() => setActiveTab('classes')}
                    >
                      <label className="mb-0 w-100 text-center" role="button">
                        Classes & Formats
                      </label>
                    </div>

                    <div
                      className={`d-flex flex-grow-1 justify-content-center align-items-center py-1 mx-1 btnTabItem ${
                        activeTab === 'global' ? 'activeBtn' : ''
                      }`}
                      onClick={() => setActiveTab('global')}
                    >
                      <label className="mb-0 w-100 text-center" role="button">
                        Global Rules
                      </label>
                    </div>
                  </div>


                  <div>
                    <CustomButton
                      btnText={t(prefix + 'openScheduleEditorBtn', 'Open Schedule Editor')}
                      type="button"
                      variant="outline-secondary"
                      className="d-flex align-items-center gap-2 m-0"
                      title={skipToManualHelperText}
                      cb={() => handleSkipToManual(values)}
                    >
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{skipToManualHelperText}</Tooltip>}
                      >
                        <InfoCircleFill />
                      </OverlayTrigger>
                    </CustomButton>
                  </div>
                </div>

                <div className="tab-content mb-4">
                  {activeTab === 'global' && tournamentInfo && (
                    <GlobalRulesSetup courts={tournamentInfo?.courts ?? []} />
                  )}
                  {activeTab === 'venues' &&
                    scheduleConstraints?.days?.map((day) => (
                      <DayVenuesSetup
                        key={`venues-day-${day.dayKey}`}
                        day={day}
                        courts={tournamentInfo!.courts}
                      />
                    ))}

                  {activeTab === 'classes' &&
                    scheduleConstraints?.days?.map((day) => (
                      <ClassFormatsSetup
                        key={`classes-day-${day.dayKey}`}
                        day={day}
                        classes={tournamentInfo!.classes}
                        courts={tournamentInfo!.courts}
                      />
                    ))}
                </div>

                <OptInError errors={errors} />
              </Form>
              );
            }}
          </Formik>
        )
      )}
    </div>
  );
};
